import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useProjectGeography } from "lib/hooks/assets/useProjectGeography";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useEffect, useRef } from "react";

/**
 * Centralized autoflight controller for camera navigation within projects.
 *
 * This hook manages automatic camera movements to prevent competing autoflight attempts.
 * Autoflight only executes in these scenarios:
 * 1. Project initial load - Flies to project bounds on first render (or page refresh within a project)
 */
export function useRouteAutoFlightController() {
  const projectId = useCurrentProjectId();
  const { cameraPlugin, terrainPlugin } = useCesium();
  const { trustedGeography } = useProjectGeography(projectId);
  const initialFlightExecuted = useRef(false);
  const lastProjectId = useRef(projectId);

  useEffect(() => {
    if (lastProjectId.current !== projectId) {
      initialFlightExecuted.current = false;
      lastProjectId.current = projectId;
    }

    if (!trustedGeography?.features.length) return;
    if (initialFlightExecuted.current) return;

    initialFlightExecuted.current = true;

    terrainPlugin.whenTerrainProviderIsReady().then(({ isCancelled }) => {
      if (isCancelled) return;

      // Note: flyToBounds returns a Promise, consider then-chaining instead of
      // async/await in useEffect, should extra actions be needed
      cameraPlugin.flyToBounds(trustedGeography.bbox);
    });
  }, [projectId, trustedGeography, terrainPlugin]);
}
